import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Snackbar,
  Alert,
  LinearProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './DrawingCreate.css';

const disciplines = [
  'Architectural',
  'Structural',
  'Mechanical',
  'Electrical',
  'Plumbing',
  'Civil',
  'Landscape',
  'Fire Protection'
];

const drawingTypes = [
  'plan',
  'elevation',
  'section',
  'detail',
  'schedule',
  'specification',
  'diagram'
];

const sheetSizes = ['A0', 'A1', 'A2', 'A3', 'A4', 'B0', 'B1', 'B2', 'B3', 'B4'];
const priorities = ['low', 'medium', 'high', 'urgent'];
const statuses = ['draft', 'under_review', 'approved', 'issued', 'superseded', 'void'];

const DrawingCreate = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    drawingNumber: '',
    title: '',
    description: '',
    discipline: '',
    projectId: '',
    scale: '',
    sheetSize: 'A1',
    drawingType: 'plan',
    status: 'draft',
    priority: 'medium',
    tags: [],
  });
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTagInput = (value) => {
    const tags = value.split(',').map(t => t.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, tags }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const showError = (message) => setSnackbar({ open: true, severity: 'error', message });
  const showSuccess = (message) => setSnackbar({ open: true, severity: 'success', message });

  const getDisciplinePrefix = (d) => {
    const map = {
      Architectural: 'A',
      Structural: 'S',
      Mechanical: 'M',
      Electrical: 'E',
      Plumbing: 'P',
      Civil: 'C',
      Landscape: 'L',
      Interior: 'I',
      'Fire Protection': 'F',
      Other: 'X'
    };
    return map[d] || 'X';
  };

  const getTypeCode = (t) => {
    const map = {
      plan: '1',
      elevation: '2',
      section: '3',
      detail: '4',
      schedule: '5',
      specification: '6',
      diagram: '7'
    };
    return map[t] || '9';
  };

  const generateDrawingNumber = () => {
    const disc = getDisciplinePrefix(formData.discipline);
    const type = getTypeCode(formData.drawingType);
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const rand = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    const generated = `${disc}${type}-${yyyy}${mm}-${rand}`;
    setFormData((prev) => ({ ...prev, drawingNumber: generated }));
    showSuccess('Drawing number generated');
  };

  const validate = () => {
    const errors = [];
    if (!formData.drawingNumber) errors.push('Drawing Number is required');
    if (!formData.title) errors.push('Title is required');
    if (!formData.discipline) errors.push('Discipline is required');
    if (!formData.sheetSize) errors.push('Sheet Size is required');
    if (!formData.drawingType) errors.push('Drawing Type is required');
    if (!formData.projectId) errors.push('Project is required');
    return errors;
  };

  // Fetch projects for selection
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        setProjectsError('');
        const resp = await api.get('/projects');
        setProjects(Array.isArray(resp.data) ? resp.data : (resp.data?.data || []));
      } catch (err) {
        setProjectsError('Failed to load projects');
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  const handleSubmit = async () => {
    const errors = validate();
    if (errors.length) {
      showError(errors.join('\n'));
      return;
    }

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'tags' && Array.isArray(formData[key])) {
          formData[key].forEach(tag => submitData.append('tags[]', tag));
        } else {
          submitData.append(key, formData[key]);
        }
      });
      // Also include legacy field name for backend routes that expect `project`
      if (formData.projectId) {
        submitData.append('project', formData.projectId);
      }
      selectedFiles.forEach(file => submitData.append('files', file));

      await api.post('/drawings', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          const progress = Math.round((evt.loaded * 100) / (evt.total || 1));
          setUploadProgress(progress);
        }
      });

      showSuccess('Drawing created successfully');
      setTimeout(() => navigate('/drawings'), 800);
    } catch (error) {
      let message = 'Failed to create drawing';
      if (error?.response?.data) {
        const data = error.response.data;
        if (typeof data === 'string') message = data;
        else if (data?.message) message = data.message;
        else if (Array.isArray(data?.errors)) {
          message = data.errors.map(e => e.msg || e.message || JSON.stringify(e)).join('\n');
        }
      }
      showError(message);
    }
  };

  return (
    <Box className="drawing-create-page" sx={{ p: 3 }}>
      <Box className="page-header">
        <Typography variant="h4" component="h1">Add Drawing</Typography>
        <Typography variant="body2" color="textSecondary">Create a new drawing and upload files</Typography>
      </Box>

      <Grid container spacing={3} className="form-grid">
        <Grid size={{ xs: 12, md: 8 }}>
          <Card className="card-equal">
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField fullWidth label="Drawing Number" value={formData.drawingNumber} onChange={(e) => handleInputChange('drawingNumber', e.target.value)} required />
                    <Button variant="outlined" onClick={generateDrawingNumber}>Generate</Button>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Discipline</InputLabel>
                    <Select value={formData.discipline} onChange={(e) => handleInputChange('discipline', e.target.value)}>
                      {disciplines.map((d) => (<MenuItem key={d} value={d}>{d}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Title" value={formData.title} onChange={(e) => handleInputChange('title', e.target.value)} required />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth multiline minRows={3} label="Description" value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Project</InputLabel>
                    <Select
                      value={formData.projectId}
                      onChange={(e) => handleInputChange('projectId', e.target.value)}
                      disabled={loadingProjects}
                    >
                      {projects.map((p) => (
                        <MenuItem key={p._id || p.id} value={p._id || p.id}>{p.name || p.title || 'Unnamed Project'}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Scale" value={formData.scale} onChange={(e) => handleInputChange('scale', e.target.value)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Sheet Size</InputLabel>
                    <Select value={formData.sheetSize} onChange={(e) => handleInputChange('sheetSize', e.target.value)}>
                      {sheetSizes.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Drawing Type</InputLabel>
                    <Select value={formData.drawingType} onChange={(e) => handleInputChange('drawingType', e.target.value)}>
                      {drawingTypes.map((t) => (<MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={formData.status} onChange={(e) => handleInputChange('status', e.target.value)}>
                      {statuses.map((s) => (<MenuItem key={s} value={s}>{s.replace('_',' ').charAt(0).toUpperCase() + s.replace('_',' ').slice(1)}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select value={formData.priority} onChange={(e) => handleInputChange('priority', e.target.value)}>
                      {priorities.map((p) => (<MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Tags (comma separated)" value={formData.tags.join(', ')} onChange={(e) => handleTagInput(e.target.value)} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box className="file-upload">
                    <Button component="label" variant="outlined">Select Files
                      <input hidden type="file" multiple onChange={handleFileSelect} />
                    </Button>
                    {selectedFiles.length > 0 && (
                      <Box className="file-list">
                        <Typography variant="caption">{selectedFiles.length} file(s) selected</Typography>
                        <Box className="chips">
                          {selectedFiles.map((f, idx) => (
                            <Chip key={idx} label={`${f.name} • ${(f.size/1024/1024).toFixed(2)} MB`} sx={{ mr: 1, mb: 1 }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <Box sx={{ mt: 2 }}>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="body2" sx={{ mt: 1 }}>Uploading... {uploadProgress}%</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card className="card-actions">
            <CardContent>
              <Box className="actions">
                <Button variant="contained" onClick={handleSubmit}>Create Drawing</Button>
                <Button variant="text" onClick={() => navigate('/drawings')}>Cancel</Button>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Attach relevant files (PDF, DWG, images) to your drawing.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DrawingCreate;